import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { User, Settings, LogOut, Menu, X } from 'lucide-react';

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <>
      <header data-testid="site-header" className="sticky top-0 z-50 bg-white border-b border-[#E5E7EB] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3" data-testid="logo-link">
              <img src="https://customer-assets.emergentagent.com/job_create-skills/artifacts/vkmcp8tc_Skillerate%20Logo.svg" alt="Skillerate" className="h-10" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/browse" className="text-[#3A1E10] hover:text-[#E98341] transition-smooth font-medium" data-testid="browse-link">
                Browse Skills
              </Link>
              {user && (
                <>
                  <Link to="/dashboard" className="text-[#3A1E10] hover:text-[#E98341] transition-smooth font-medium" data-testid="dashboard-link">
                    Dashboard
                  </Link>
                  <Link to="/builder" className="text-[#3A1E10] hover:text-[#E98341] transition-smooth font-medium" data-testid="builder-link">
                    Create Skill
                  </Link>
                </>
              )}
            </nav>

            {/* Desktop User Menu */}
            <div className="hidden md:flex items-center space-x-4">
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid="user-menu-button" className="flex items-center space-x-2 hover:opacity-80 transition-smooth">
                      <Avatar>
                        <AvatarImage src={user.avatar_url} />
                        <AvatarFallback className="gradient-primary text-white">{user.username?.[0]?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <span className="font-medium text-[#3A1E10]">{user.username}</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onClick={() => navigate(`/profile/${user.id}`)} data-testid="profile-menu-item">
                      <User className="mr-2 h-4 w-4" />
                      <span>My Profile</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-menu-item">
                      <Settings className="mr-2 h-4 w-4" />
                      <span>Settings</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Logout</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <Button onClick={() => navigate('/login')} variant="outline" className="border-[#E98341] text-[#E98341] hover:bg-[#E98341] hover:text-white" data-testid="sign-in-button">
                    Sign In
                  </Button>
                  <Button onClick={() => navigate('/signup')} className="gradient-primary text-white" data-testid="get-started-button">
                    Get Started
                  </Button>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-[#3A1E10]"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-[#E5E7EB]" data-testid="mobile-menu">
              <Link to="/browse" className="block py-2 text-[#3A1E10] hover:text-[#E98341]" onClick={() => setMobileMenuOpen(false)}>
                Browse Skills
              </Link>
              {user ? (
                <>
                  <Link to="/dashboard" className="block py-2 text-[#3A1E10] hover:text-[#E98341]" onClick={() => setMobileMenuOpen(false)}>
                    Dashboard
                  </Link>
                  <Link to="/builder" className="block py-2 text-[#3A1E10] hover:text-[#E98341]" onClick={() => setMobileMenuOpen(false)}>
                    Create Skill
                  </Link>
                  <Link to={`/profile/${user.id}`} className="block py-2 text-[#3A1E10] hover:text-[#E98341]" onClick={() => setMobileMenuOpen(false)}>
                    My Profile
                  </Link>
                  <Link to="/settings" className="block py-2 text-[#3A1E10] hover:text-[#E98341]" onClick={() => setMobileMenuOpen(false)}>
                    Settings
                  </Link>
                  <button onClick={handleLogout} className="block w-full text-left py-2 text-[#3A1E10] hover:text-[#E98341]">
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => { navigate('/login'); setMobileMenuOpen(false); }} className="block w-full text-left py-2 text-[#3A1E10] hover:text-[#E98341]">
                    Sign In
                  </button>
                  <button onClick={() => { navigate('/signup'); setMobileMenuOpen(false); }} className="block w-full text-left py-2 text-[#E98341] font-medium">
                    Get Started
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default Header;